/* eslint-disable react/require-default-props */
import React from 'react';
import { InputGroup, InputLeftElement, Box, Input, Select, HStack } from '@chakra-ui/core';
import { IoIosSearch } from 'react-icons/io';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

type SearchMode = 'candidate' | 'business';

interface CandidateLocationSearchInputProps {
  onChange: (searchQuery: string, searchMode: SearchMode) => void;
  defaultValue?: string;
  debounceMs?: number;
}

const BORDER_COLOR = '#97979752';
const PLACEHOLDER_COLOR = '#A0AEC0';
const FOCUS_BORDER_COLOR = '#6aa1e9ff';

export const CandidateLocationSearchInput = React.memo(function CandidateLocationSearchInput({
  onChange,
  defaultValue,
  debounceMs = 300,
}: CandidateLocationSearchInputProps) {
  const { t } = useTranslation();
  const [searchMode, setSearchMode] = React.useState<SearchMode>('candidate');
  const [inputValue, setInputValue] = React.useState(defaultValue);

  const SEARCH_MODES: { label: string; value: SearchMode }[] = [
    { label: t('search.candidate'), value: 'candidate' },
    { label: t('search.business'), value: 'business' },
  ];

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedOnChange = React.useRef(
    debounce(
      /* istanbul ignore next */ (query: string, mode: SearchMode) => {
        // istanbul ignore next
        onChangeRef.current(query, mode);
      },
      debounceMs,
    ),
  );

  React.useEffect(() => {
    debouncedOnChange.current = debounce((query: string, mode: SearchMode) => {
      onChangeRef.current(query, mode);
    }, debounceMs);
  }, [debounceMs]);

  React.useEffect(() => () => debouncedOnChange.current.cancel(), []);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setInputValue(query);
      debouncedOnChange.current(query, searchMode);
    },
    [searchMode],
  );

  const handleSelectChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMode = e.target.value as SearchMode;
      setSearchMode(newMode);
      debouncedOnChange.current.cancel();
      onChangeRef.current(inputValue || /* istanbul ignore next */ '', newMode);
    },
    [inputValue],
  );

  return (
    <HStack
      spacing={1}
      divider={<Box w="1px" bg={BORDER_COLOR} h="-webkit-fill-available" />}
      sx={{
        borderRadius: 'full',
        border: '1px solid',
        borderColor: BORDER_COLOR,
        paddingLeft: 2,
        height: '2.125rem',
        '&:focus-within': { borderColor: FOCUS_BORDER_COLOR },
      }}
    >
      <Select
        value={searchMode}
        onChange={handleSelectChange}
        color={PLACEHOLDER_COLOR}
        variant="unstyled"
        fontSize="sm"
        width="auto"
        minW="0"
        pl={0}
        pr={0}
        flexShrink={0}
        height="-webkit-fill-available"
      >
        {SEARCH_MODES.map(({ label, value }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>

      <InputGroup role="group" variant="unstyled" display="flex" alignItems="center" height="-webkit-fill-available">
        <InputLeftElement position="relative">
          <IoIosSearch size={18} color={PLACEHOLDER_COLOR} />
        </InputLeftElement>
        <Input
          type="search"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={t(searchMode === 'candidate' ? 'search.candidateSearchLabel' : 'search.businessSearchLabel')}
          _placeholder={{ color: PLACEHOLDER_COLOR }}
          rounded="full"
          fontSize="sm"
          px={1}
          height="-webkit-fill-available"
        />
      </InputGroup>
    </HStack>
  );
});
